import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSelect } from '../components/LanguageSelect';
import { ChainSelect } from '../components/form/ChainSelect';
import { WalletSelect } from '../components/form/WalletSelect';
import {
  inputFieldClass,
  inputMonoFieldClass,
  labelClass,
  primarySubmitButtonClass,
  selectFieldClass,
} from '../components/form/fieldClasses';
import {
  getJSON,
  postJSON,
  type CatalogResponse,
  type CatalogToken,
  type SwapExchangePrepareResponse,
  type SwapExchangeSubmitResponse,
  type SwapPoolQuote,
  type SwapQuoteResponse,
  type TransferNetworkResponse,
  type TransferPendingNonceResponse,
  type UserProfile,
  type WatchedTokenBalancesResponse,
  type WalletsResponse,
  userWalletApiBase,
} from '../lib/api';
import { explorerAddressUrl, explorerTxUrl } from '../lib/explorers';
import { prefillEip1559Gwei } from '../lib/gasFees';
import { useWalletSessionUnlocked } from '../hooks/useWalletSessionUnlocked';
import { isWalletSessionUnlocked } from '../lib/mpcUnlockCache';
import { signDigestWithWalletMpc } from '../lib/mpcSession';
import { appMainClass, pageWrapClass } from '../layout/page';

/** Undefined = omit (server uses pending nonce); throws if non-empty but invalid. */
function parseNonceInput(raw: string): number | undefined {
  const s = raw.trim();
  if (!s) {
    return undefined;
  }
  if (!/^\d+$/.test(s)) {
    throw new Error('INVALID_NONCE');
  }
  const n = Number(s);
  if (!Number.isSafeInteger(n) || n < 0) {
    throw new Error('INVALID_NONCE');
  }
  return n;
}

function tokenKey(t: CatalogToken): string {
  return `${t.chain}\t${t.token_ref}`;
}

export default function SwapPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [wallets, setWallets] = useState<WalletsResponse['wallets']>([]);
  const [catalog, setCatalog] = useState<CatalogToken[]>([]);
  const [walletId, setWalletId] = useState<number>(0);
  const payUnlocked = useWalletSessionUnlocked(walletId);
  const [chain, setChain] = useState<string>('ethereum');
  const [tokenInRef, setTokenInRef] = useState<string>('native');
  const [tokenOutRef, setTokenOutRef] = useState<string>('');
  const [amountIn, setAmountIn] = useState<string>('1');
  const [slippageBps, setSlippageBps] = useState<number>(50);
  const [quoteBusy, setQuoteBusy] = useState(false);
  const [quote, setQuote] = useState<SwapQuoteResponse | null>(null);
  const [exchangeMaxFeeGwei, setExchangeMaxFeeGwei] = useState('2');
  const [exchangePriorityFeeGwei, setExchangePriorityFeeGwei] = useState('1');
  const [paymentPassword, setPaymentPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [profile, setProfile] = useState<Pick<UserProfile, 'mfa_enabled'> | null>(null);
  const [swapBusyRow, setSwapBusyRow] = useState<string | null>(null);
  const [swapTxHash, setSwapTxHash] = useState('');
  const [msg, setMsg] = useState('');
  const [msgOk, setMsgOk] = useState(false);
  const [sellBalanceLine, setSellBalanceLine] = useState<string>('');
  const [sellBalanceTone, setSellBalanceTone] = useState<'muted' | 'warn' | 'err'>('muted');
  const sellBalanceFetchGen = useRef(0);
  const [nonceInput, setNonceInput] = useState('');
  const [nonceLoadErr, setNonceLoadErr] = useState('');
  const nonceReqGen = useRef(0);
  const nonceDirtyRef = useRef(false);
  const feeReqGen = useRef(0);

  const loadBootstrap = useCallback(async () => {
    setLoading(true);
    setMsg('');
    try {
      const [wr, cr, pr] = await Promise.all([
        getJSON<WalletsResponse>('/api/user/wallets'),
        getJSON<CatalogResponse>('/api/user/swap/catalog'),
        getJSON<UserProfile>('/api/user/profile'),
      ]);
      if (wr.res.status === 401 || pr.res.status === 401) {
        navigate('/', { replace: true });
        return;
      }
      if (wr.res.status === 403 || pr.res.status === 403) {
        navigate('/home', { replace: true });
        return;
      }
      if (pr.res.ok) {
        setProfile({ mfa_enabled: pr.data.mfa_enabled });
      } else {
        setProfile(null);
      }
      if (!wr.res.ok) {
        setMsgOk(false);
        setMsg(wr.data.error || t('common.error'));
        setWallets([]);
      } else {
        setWallets(wr.data.wallets ?? []);
      }
      setCatalog(cr.res.ok ? (cr.data.tokens ?? []) : []);
      if (!cr.res.ok) {
        setMsgOk(false);
        setMsg((m) => m || cr.data.error || t('common.error'));
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, t]);

  useEffect(() => {
    void loadBootstrap();
  }, [loadBootstrap, i18n.language]);

  useEffect(() => {
    const raw = searchParams.get('wallet');
    const n = raw ? Number(raw) : NaN;
    if (!Number.isFinite(n) || n <= 0 || wallets.length === 0) {
      return;
    }
    if (!wallets.some((w) => w.id === n)) {
      return;
    }
    setWalletId(n);
  }, [searchParams, wallets]);

  const tokensOnChain = useMemo(
    () => catalog.filter((c) => c.chain === chain).sort((a, b) => a.symbol.localeCompare(b.symbol)),
    [catalog, chain],
  );

  useEffect(() => {
    if (tokensOnChain.length === 0) {
      setTokenInRef('native');
      setTokenOutRef('');
      return;
    }
    if (!tokensOnChain.some((x) => x.token_ref === tokenInRef)) {
      setTokenInRef(tokensOnChain[0].token_ref);
      return;
    }
    if (tokenOutRef === tokenInRef || !tokensOnChain.some((x) => x.token_ref === tokenOutRef)) {
      const o = tokensOnChain.find((x) => x.token_ref !== tokenInRef);
      if (o) {
        setTokenOutRef(o.token_ref);
      }
    }
  }, [tokensOnChain, tokenInRef, tokenOutRef]);

  useEffect(() => {
    nonceDirtyRef.current = false;
  }, [walletId, chain]);

  useEffect(() => {
    if (!walletId || !chain) {
      setNonceInput('');
      setNonceLoadErr('');
      return;
    }
    const gen = ++nonceReqGen.current;
    setNonceLoadErr('');
    void (async () => {
      const { res, data } = await postJSON<TransferPendingNonceResponse>('/api/user/transfer/pending-nonce', {
        wallet_id: walletId,
        chain,
      });
      if (gen !== nonceReqGen.current) {
        return;
      }
      if (!res.ok) {
        setNonceLoadErr(data.error || t('transfer.nonceLoadError'));
        return;
      }
      if (nonceDirtyRef.current) {
        return;
      }
      const n = data.nonce;
      if (typeof n === 'number' && Number.isFinite(n)) {
        setNonceInput(String(n));
      }
    })();
  }, [walletId, chain, t]);

  useEffect(() => {
    if (!walletId || !chain || !tokenInRef) {
      setSellBalanceLine('');
      return;
    }
    const gen = ++sellBalanceFetchGen.current;
    setSellBalanceLine(t('swap.sellBalanceLoading'));
    setSellBalanceTone('muted');
    void (async () => {
      const { res, data } = await postJSON<WatchedTokenBalancesResponse>(
        `${userWalletApiBase(walletId)}/tokens/balances`,
        { tokens: [{ chain, token_ref: tokenInRef }] },
      );
      if (gen !== sellBalanceFetchGen.current) {
        return;
      }
      if (!res.ok) {
        setSellBalanceTone('err');
        setSellBalanceLine(data.error || t('common.error'));
        return;
      }
      const row = data.balances?.[0];
      if (!row) {
        setSellBalanceTone('muted');
        setSellBalanceLine('');
        return;
      }
      const sym = row.symbol?.trim() || '';
      if (row.balance_error) {
        setSellBalanceTone('warn');
        setSellBalanceLine(`${t('swap.sellBalance')}: ${row.balance_error}`);
        return;
      }
      setSellBalanceTone('muted');
      const amt = row.balance_formatted?.trim() || '0';
      setSellBalanceLine(
        sym ? `${t('swap.sellBalance')}: ${amt} ${sym}` : `${t('swap.sellBalance')}: ${amt}`,
      );
    })();
  }, [walletId, chain, tokenInRef, t]);

  useEffect(() => {
    const gen = ++feeReqGen.current;
    void (async () => {
      const { res, data } = await postJSON<TransferNetworkResponse>('/api/user/transfer/network', { chain });
      if (gen !== feeReqGen.current || !res.ok) {
        return;
      }
      const suggested = prefillEip1559Gwei(data.gas_price_gwei, data.base_fee_gwei);
      if (suggested) {
        setExchangeMaxFeeGwei(suggested.maxFeeGwei);
        setExchangePriorityFeeGwei(suggested.priorityFeeGwei);
      }
    })();
  }, [chain]);

  const onChainChange = (next: string) => {
    setChain(next);
    setQuote(null);
  };

  async function onQuote(e: FormEvent) {
    e.preventDefault();
    if (!walletId || tokenInRef === tokenOutRef) {
      return;
    }
    setQuoteBusy(true);
    setMsg('');
    setQuote(null);
    try {
      const { res, data } = await postJSON<SwapQuoteResponse>('/api/user/swap/quote', {
        wallet_id: walletId,
        chain,
        token_in: tokenInRef,
        token_out: tokenOutRef,
        amount_in: amountIn.trim(),
        slippage_bps: slippageBps,
      });
      if (res.status === 401) {
        navigate('/', { replace: true });
        return;
      }
      if (!res.ok) {
        setMsgOk(false);
        setMsg(data.error || t('common.error'));
        return;
      }
      setQuote(data);
      setMsgOk(true);
      setMsg(t('swap.quoteOk'));
    } finally {
      setQuoteBusy(false);
    }
  }

  function swapRowCanExecute(p: SwapPoolQuote): boolean {
    if (!p.quote_error && p.pool_address?.trim()) {
      const v = (p.version || '').toLowerCase();
      return v === 'v2' || v === 'v3';
    }
    return false;
  }

  async function onSwapRow(p: SwapPoolQuote) {
    if (!walletId || !swapRowCanExecute(p)) {
      return;
    }
    const payPwd = paymentPassword.trim();
    if (!payPwd && !payUnlocked && !(await isWalletSessionUnlocked(walletId))) {
      setMsgOk(false);
      setMsg(t('swap.payPasswordRequired'));
      return;
    }
    let nonceExplicit: number | undefined;
    try {
      nonceExplicit = parseNonceInput(nonceInput);
    } catch {
      setMsgOk(false);
      setMsg(t('transfer.nonceInvalid'));
      return;
    }
    if (!quote || !poolKey(p)) {
      return;
    }
    const busyKey = poolKey(p)!;
    setSwapBusyRow(busyKey);
    setSwapTxHash('');
    setMsg('');
    try {
      const prepPayload: Record<string, unknown> = {
        wallet_id: walletId,
        chain,
        token_in: tokenInRef,
        token_out: tokenOutRef,
        amount_in: amountIn.trim(),
        slippage_bps: slippageBps,
        pool_address: p.pool_address!.trim(),
        version: p.version.trim(),
        max_fee_gwei: exchangeMaxFeeGwei.trim(),
        priority_fee_gwei: exchangePriorityFeeGwei.trim(),
      };
      if (nonceExplicit !== undefined) {
        prepPayload.nonce = nonceExplicit;
      }
      const { res: prepRes, data: prep } = await postJSON<SwapExchangePrepareResponse>(
        '/api/user/swap/exchange/prepare',
        prepPayload,
      );
      if (!prepRes.ok) {
        setMsgOk(false);
        setMsg(prep.error || t('common.error'));
        return;
      }
      const signOut = await signDigestWithWalletMpc(walletId, prep.digest_hex, payPwd, {
        commonErrorMessage: t('common.error'),
        sessionMissingMessage: t('transfer.sessionMissing'),
        sessionInvalidMessage: t('transfer.sessionInvalid'),
        mfaCode: profile?.mfa_enabled ? mfaCode.trim() : undefined,
      });
      const nonceForSubmit = nonceExplicit ?? prep.nonce;
      const { res: subRes, data: subData } = await postJSON<SwapExchangeSubmitResponse>(
        '/api/user/swap/exchange/submit',
        {
          wallet_id: walletId,
          chain,
          token_in: tokenInRef,
          token_out: tokenOutRef,
          amount_in: amountIn.trim(),
          slippage_bps: slippageBps,
          pool_address: p.pool_address!.trim(),
          version: p.version.trim(),
          max_fee_gwei: exchangeMaxFeeGwei.trim(),
          priority_fee_gwei: exchangePriorityFeeGwei.trim(),
          nonce: nonceForSubmit,
          gas_limit: prep.gas_limit,
          digest_hex: prep.digest_hex,
          r_hex: signOut.r_hex,
          s_hex: signOut.s_hex,
          v: signOut.v,
        },
      );
      if (!subRes.ok) {
        setMsgOk(false);
        setMsg(subData.error || t('common.error'));
        return;
      }
      setMsgOk(true);
      setMsg(subData.message || t('swap.swapSubmitted'));
      setSwapTxHash(subData.tx_hash ?? '');
      setPaymentPassword('');
      setMfaCode('');
    } catch (err) {
      setMsgOk(false);
      setMsg(String(err));
    } finally {
      setSwapBusyRow(null);
    }
  }

  function poolKey(p: SwapPoolQuote): string | null {
    const a = p.pool_address?.trim();
    if (!a) {
      return null;
    }
    return `${p.version}:${a}`;
  }

  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-slate-100 text-slate-600">{t('home.loading')}</div>
    );
  }

  return (
    <div className={pageWrapClass}>
      <main className={appMainClass}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Link to="/home" className="text-sm font-medium text-blue-600 hover:underline">
            ← {t('swap.backHome')}
          </Link>
          <LanguageSelect />
        </div>

        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h1 className="text-2xl font-semibold text-slate-900">{t('swap.title')}</h1>
          <p className="mt-1 text-sm text-slate-600">{t('swap.subtitle')}</p>

          {msg ? (
            <div
              role="status"
              className={`mt-4 rounded-xl border px-4 py-3 text-sm font-medium ${
                msgOk ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-red-200 bg-red-50 text-red-800'
              }`}
            >
              {msg}
            </div>
          ) : null}

          <form className="mt-6 space-y-4" onSubmit={(e) => void onQuote(e)}>
            <div>
              <label htmlFor="swap-wallet" className={labelClass}>
                {t('swap.selectWallet')}
              </label>
              <WalletSelect
                id="swap-wallet"
                wallets={wallets}
                value={walletId}
                onChange={setWalletId}
                placeholder={t('swap.walletPlaceholder')}
              />
            </div>

            <div>
              <label htmlFor="swap-chain" className={labelClass}>
                {t('swap.chain')}
              </label>
              <ChainSelect id="swap-chain" value={chain} onChange={onChainChange} />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="swap-in" className={labelClass}>
                  {t('swap.tokenIn')}
                </label>
                <select
                  id="swap-in"
                  className={selectFieldClass}
                  value={tokenInRef}
                  onChange={(e) => {
                    setTokenInRef(e.target.value);
                    setQuote(null);
                  }}
                >
                  {tokensOnChain.map((tok) => (
                    <option key={tokenKey(tok)} value={tok.token_ref}>
                      {tok.symbol} — {tok.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="swap-out" className={labelClass}>
                  {t('swap.tokenOut')}
                </label>
                <select
                  id="swap-out"
                  className={selectFieldClass}
                  value={tokenOutRef}
                  onChange={(e) => {
                    setTokenOutRef(e.target.value);
                    setQuote(null);
                  }}
                >
                  {tokensOnChain
                    .filter((tok) => tok.token_ref !== tokenInRef)
                    .map((tok) => (
                      <option key={tokenKey(tok)} value={tok.token_ref}>
                        {tok.symbol} — {tok.name}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            <div>
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                <label htmlFor="swap-amt" className={labelClass}>
                  {t('swap.amountIn')}
                </label>
                {walletId > 0 && sellBalanceLine ? (
                  <span
                    className={`text-xs tabular-nums ${
                      sellBalanceTone === 'err'
                        ? 'text-red-600'
                        : sellBalanceTone === 'warn'
                          ? 'text-amber-700'
                          : 'text-slate-500'
                    }`}
                  >
                    {sellBalanceLine}
                  </span>
                ) : null}
              </div>
              <input
                id="swap-amt"
                type="text"
                inputMode="decimal"
                autoComplete="off"
                spellCheck={false}
                className={inputMonoFieldClass}
                value={amountIn}
                onChange={(e) => {
                  setAmountIn(e.target.value);
                  setQuote(null);
                }}
              />
            </div>

            <div>
              <label htmlFor="swap-slip" className={labelClass}>
                {t('swap.slippage')} ({t('swap.slippageBps', { bps: slippageBps })})
              </label>
              <input
                id="swap-slip"
                type="number"
                min={1}
                max={5000}
                step={1}
                className={inputFieldClass}
                value={slippageBps}
                onChange={(e) => setSlippageBps(Number(e.target.value) || 50)}
              />
              <p className="mt-1 text-xs text-slate-500">{t('swap.slippageHint')}</p>
            </div>

            <div>
              <label htmlFor="swap-nonce" className={labelClass}>
                {t('transfer.nonce')}
              </label>
              <input
                id="swap-nonce"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                spellCheck={false}
                className={inputMonoFieldClass}
                placeholder="0"
                value={nonceInput}
                onChange={(e) => {
                  nonceDirtyRef.current = true;
                  setNonceInput(e.target.value);
                }}
              />
              <p className="mt-1 text-xs text-slate-500">{t('transfer.nonceHint')}</p>
              {nonceLoadErr ? <p className="mt-1 text-xs text-amber-700">{nonceLoadErr}</p> : null}
            </div>

            <button
              type="submit"
              disabled={quoteBusy || !walletId || tokenInRef === tokenOutRef || tokensOnChain.length < 2}
              className={primarySubmitButtonClass}
            >
              {quoteBusy ? t('swap.quoting') : t('swap.getQuote')}
            </button>
          </form>
        </section>

        {quote ? (
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900">{t('swap.resultsTitle')}</h2>
            <p className="mt-2 text-sm text-slate-600">
              <span className="font-medium text-slate-800">{quote.wallet_name}</span>
              {' · '}
              {t(`wallet.chain.${quote.chain}`)}
            </p>
            <dl className="mt-3 grid gap-2 text-sm text-slate-700 sm:grid-cols-2">
              <div>
                <dt className="text-xs uppercase tracking-wide text-slate-500">{t('swap.gasPrice')}</dt>
                <dd className="font-mono">
                  {quote.gas_price_gwei ? `${quote.gas_price_gwei} Gwei` : '—'}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-slate-500">{t('swap.amountInSummary')}</dt>
                <dd className="font-mono">
                  {quote.amount_in_formatted} {quote.token_in_symbol}
                </dd>
              </div>
            </dl>

            <div className="mt-5 grid gap-4 sm:grid-cols-3">
              <div>
                <label htmlFor="swap-ex-max" className={labelClass}>
                  {t('transfer.maxFee')}
                </label>
                <input
                  id="swap-ex-max"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  spellCheck={false}
                  className={inputMonoFieldClass}
                  value={exchangeMaxFeeGwei}
                  onChange={(e) => setExchangeMaxFeeGwei(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="swap-ex-prio" className={labelClass}>
                  {t('transfer.priorityFee')}
                </label>
                <input
                  id="swap-ex-prio"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  spellCheck={false}
                  className={inputMonoFieldClass}
                  value={exchangePriorityFeeGwei}
                  onChange={(e) => setExchangePriorityFeeGwei(e.target.value)}
                />
              </div>
              <div className="sm:col-span-3">
                <label htmlFor="swap-paypwd" className={labelClass}>
                  {t('swap.payPassword')}
                </label>
                <input
                  id="swap-paypwd"
                  required={walletId > 0 ? !payUnlocked : true}
                  type="password"
                  autoComplete="current-password"
                  className={`${inputFieldClass} max-w-md`}
                  value={paymentPassword}
                  onChange={(e) => setPaymentPassword(e.target.value)}
                  placeholder={walletId > 0 && payUnlocked ? t('transfer.payPwdUnlockHint') : ''}
                />
                <p className="mt-1 text-xs text-slate-500">
                  {walletId > 0 && payUnlocked
                    ? t('transfer.payPwdUnlockHint')
                    : t('swap.payPasswordHint')}
                </p>
              </div>
              {profile?.mfa_enabled ? (
                <div className="sm:col-span-3">
                  <label htmlFor="swap-mfa" className={labelClass}>
                    {t('mfa.code')}
                  </label>
                  <input
                    id="swap-mfa"
                    required
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    className={`${inputFieldClass} max-w-md`}
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value)}
                  />
                </div>
              ) : null}
            </div>

            {swapTxHash ? (
              <p className="mt-3 break-all text-sm text-slate-700">
                {t('transfer.txHash')}:{' '}
                {explorerTxUrl(chain, swapTxHash) ? (
                  <a
                    href={explorerTxUrl(chain, swapTxHash)!}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-blue-700 underline"
                  >
                    {swapTxHash}
                  </a>
                ) : (
                  <span className="font-mono">{swapTxHash}</span>
                )}
              </p>
            ) : null}

            <div className="mt-6 overflow-x-auto rounded-xl border border-slate-100">
              <table className="w-full min-w-[42rem] text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="px-3 py-2 font-medium text-slate-700">{t('swap.colProtocol')}</th>
                    <th className="px-3 py-2 font-medium text-slate-700">{t('swap.colOut')}</th>
                    <th className="px-3 py-2 font-medium text-slate-700">{t('swap.colMinOut')}</th>
                    <th className="px-3 py-2 font-medium text-slate-700">{t('swap.colLiquidity')}</th>
                    <th className="px-3 py-2 font-medium text-slate-700">{t('swap.colNote')}</th>
                    <th className="px-3 py-2 font-medium text-slate-700">{t('swap.colAction')}</th>
                  </tr>
                </thead>
                <tbody>
                  {quote.pools.map((p, i) => {
                    const poolHref =
                      p.pool_address?.trim() && quote.chain
                        ? explorerAddressUrl(quote.chain, p.pool_address)
                        : null;
                    return (
                    <tr key={`${p.version}-${i}`} className="border-b border-slate-100 last:border-b-0">
                      <td className="px-3 py-2 align-top">
                        {poolHref ? (
                          <a
                            href={poolHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex flex-wrap items-baseline gap-x-1 font-medium text-blue-600 hover:underline"
                            title={t('swap.openPoolInExplorer')}
                          >
                            <span>{p.protocol}</span>
                            <span className="font-normal text-slate-400">·</span>
                            <span className="font-normal text-slate-600">{p.version.toUpperCase()}</span>
                          </a>
                        ) : (
                          <>
                            <span className="font-medium text-slate-900">{p.protocol}</span>
                            <span className="text-slate-400"> · </span>
                            <span className="text-slate-500">{p.version.toUpperCase()}</span>
                          </>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top font-mono text-xs text-slate-800 sm:text-sm">
                        {p.amount_out_formatted ? `${p.amount_out_formatted} ${quote.token_out_symbol}` : '—'}
                      </td>
                      <td className="px-3 py-2 align-top font-mono text-xs text-slate-800 sm:text-sm">
                        {p.amount_out_min_formatted ? `${p.amount_out_min_formatted} ${quote.token_out_symbol}` : '—'}
                      </td>
                      <td className="px-3 py-2 align-top text-slate-600">{p.liquidity_note || '—'}</td>
                      <td className="px-3 py-2 align-top text-xs text-amber-900">{p.quote_error || '—'}</td>
                      <td className="px-3 py-2 align-top">
                        <button
                          type="button"
                          disabled={
                            !walletId ||
                            swapBusyRow !== null ||
                            !swapRowCanExecute(p)
                          }
                          onClick={() => void onSwapRow(p)}
                          className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                        >
                          {swapBusyRow === poolKey(p) ? t('swap.swapBusy') : t('swap.swapExecute')}
                        </button>
                        {!swapRowCanExecute(p) && (p.quote_error || !p.pool_address) ? (
                          <p className="mt-1 max-w-[10rem] text-[10px] leading-snug text-slate-400">
                            {t('swap.swapCannotRow')}
                          </p>
                        ) : null}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        ) : null}
      </main>
    </div>
  );
}
